import { Reducer } from 'redux'
import type {
  AzureOfferPlan,
  AzurePricingType,
  ChannelType,
  Dimension,
  EulaType,
  FieldMetadata,
  Installments,
  MetaData,
  OfferRecipients,
  PriceTypes,
  ScheduleType,
} from '../../../flyout/modules/privateOffer/reducer'
import { ResaleAuthorizationsActionTypes } from './actionTypes'
import type {
  MPPOResaleAuthAuditJson,
  ResaleAuthorizationType,
  FailedErrorMessage,
} from '../../../flyout/modules/receivedResellerAuthorization/reducer'
import type { CloudMarketplace } from '../../../flyout/modules/productsListing/reducer'

export interface MicrosoftResellerDetails {
  id: string
  partnerName: string
  location?: string
}
export interface ResaleAuthorizationsResellerAgreements {
  id: string
  sellerName: string
  crmAwsResellerAgreementObjectId: string
  agreementDescription: string
  mppoDescription?: string
  agreementName: string
  buyerAwsAccountId: string
  agreementStartDate?: string
  agreementExpirationDate: string
  emailCustomMessage: string
  contractDurationInMonthsForEndCustomer?: number
  agreementDurationType: AgreementDurationType
  discountType: DiscountType
  contractDurationType: ContractDurationType
  endCustomerEulaType: EulaType
  endCustomerEulaFiles: string[]
  resellerLicenseAgreementType: EulaType
  resellerLicenseAgreementFiles: string[]
  percentageDiscount?: number
  resellersInfo: ResellerInfo[]
  awsAgreementId: string
  agreementApprovers: AgreementApprovers[]
  offerApprovers?: AgreementApprovers[]
  agreementRecipients: OfferRecipients[]
  offerRecipients?: OfferRecipients[]
  agreementStakeholders: Stakeholders[]
  offerStakeholders: Stakeholders[]
  customOfferStakeholders?: Stakeholders[]
  installmentInfo: Installments[]
  metaData: MetaData[]
  products: CPPOProducts[]
  renewal?: boolean
  renewalChannel?: ChannelType | null
  createdAt: string
  status: ResellerAgreementStatus
  resellerName: string
  draft?: boolean
  pricesArray?: [keyof PriceTypes]
  dimensions?: Dimension[]
  companyName?: string
  awsProductId?: string
  origin?: ResaleAuthorizationType
  serviceStartDate?: string
  updatedAt?: string
  error?: string
  cloudProductName?: string
  resellerDetails?: MicrosoftResellerDetails[]
  azurePricingType?: AzurePricingType
  plans?: AzureOfferPlan[]
  beneficiaryId?: string
  variableStartDate?: boolean
  existingContractRenewal?: boolean
  offerStartDate?: string
  acceptBy?: string
  mppoName?: string
  eulaType?: EulaType
  eulaFiles?: string[]
  offerExpirationDate?: string
  sellerId?: string
  labraOfferUrl?: string
  acceptanceLink?: string
  azureMppoId?: string
  offerStatus?: ResellerAgreementStatus
  productName?: string
  statusLogs?: MPPOResaleAuthAuditJson
  errorDetails?: {
    errors?: FailedErrorMessage[]
  }
  labraProductId?: string
  offerEndBuyers?: OfferEndBuyers[]
  currency?: string
  pricingModel?: string
  renewalPricingModel?: string | null
  contractDurationTimeUnit?: string
  maximumOfferStartDate?: string
  flexiblePaymentType?: ScheduleType
  firstInstallmentDate?: string
  tcv?: number
  maxAgreementStartDate?: string
  cloud?: CloudMarketplace
  fieldMetadata?: FieldMetadata
  cloudAccountId?: string
  netPaymentTerms?: string
  resellerAuthorizationId?: string
}

export type OfferEndBuyers = {
  firstName: string
  lastName: string
  title: string
  email: string
  sentAt?: string
  lastViewedAt?: string
  firstViewedAt?: string
  crmObjectId?: string
  name?: string
}

export const resaleAuthorizationStatusMicrosoft: { [key: string]: string } = {
  //Microsoft status
  'Sent to Reseller': 'OFFER_SENT_RESELLER',
  'Viewed by Reseller': 'OFFER_VIEWED_RESELLER',
  'Extended to buyer': 'OFFER_EXTENDED_TO_BUYER',
  'Pending purchase': 'OFFER_TERMS_ACCEPTED_BUYER',
  'Pending account configuration': 'OFFER_PURCHASE_COMPLETE',
  Accepted: 'OFFER_ACCEPTED',
}

export enum ResellerAgreementStatus {
  //AWS status
  IN_PROGRESS = 'in_progress',
  ACTIVE = 'active',
  EXPIRED = 'expired',
  COMPLETED = 'completed',
  RESTRICTED = 'restricted',
  DEACATIVATION_IN_PROGRESS = 'deactivation_in_progress',
  DEACATIVATED = 'deactivated',
  FAILED = 'failed',
  DRAFT = 'draft',
  PENDING = 'pending',
  CHANNEL_OFFER_ACCEPTED = 'channel_offer_accepted',

  //Microsoft status
  OFFER_IN_DRAFT = 'OFFER_IN_DRAFT',
  OFFER_CREATION_IN_PROGRESS = 'OFFER_CREATION_IN_PROGRESS',
  OFFER_CREATION_FAILED = 'OFFER_CREATION_FAILED',
  OFFER_SENT_RESELLER = 'OFFER_SENT_RESELLER',
  OFFER_VIEWED_RESELLER = 'OFFER_VIEWED_RESELLER',
  OFFER_EXTENDED_TO_BUYER = 'OFFER_EXTENDED_TO_BUYER',
  OFFER_TERMS_ACCEPTED_BUYER = 'OFFER_TERMS_ACCEPTED_BUYER',
  OFFER_PURCHASE_COMPLETE = 'OFFER_PURCHASE_COMPLETE',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
  OFFER_EXPIRED = 'OFFER_EXPIRED',
  OFFER_RESTRICTION_IN_PROGRESS = 'OFFER_RESTRICTION_IN_PROGRESS',
  OFFER_RESTRICTED = 'OFFER_RESTRICTED',
}
export enum AgreementDurationType {
  SINGLE_USE = 'SINGLE_USE',
  SPECIFIC_TIME = 'SPECIFIC_TIME',
  NO_SET_TIME = 'NO_SET_TIME',
}
export enum DiscountType {
  DISCOUNT = 'DISCOUNT',
  INDIVIDUAL_PRICING = 'INDIVIDUAL_PRICING',
  FLEXIBLE_PAYMENT_TERMS = 'FLEXIBLE_PAYMENT_TERMS',
  USAGE_PRICING = 'USAGE_PRICING',
}
export enum ContractDurationType {
  STANDARD = 'standard',
  CUSTOM = 'custom',
}
export enum ResellerLicenseAgreementType {
  STANDARD = 'standard',
  CUSTOM = 'custom',
}
export type AWSPartnerTier = 'registered' | 'select' | 'advanced' | 'premier'
export interface ResellerInfo {
  resellerCompanyName?: string
  labraPlatformResellerId?: string
  geoLocation?: string
  resellerAwsAccountId?: string
  crmAwsResellerObjectId?: string
  awsPartnerTier?: AWSPartnerTier
  companyName?: string
}
export interface AgreementApprovers {
  name: string
  email: string
  hierarchyOrder: number
}

export interface Stakeholders {
  firstName: string
  lastName: string
  title: string
  email: string
}

export interface CPPOProducts {
  awsProductId: string
  dimensions: Dimension[]
  productName?: string
}

export interface ResaleAuthorizationsState {
  resellerAgreements: ResaleAuthorizationsResellerAgreements[]
  count: number
  pageNumber: number
  noOfPage: number | null
  pageSize: number
  currentResaleAuthorization: {
    [x: string]: ResaleAuthorizationsResellerAgreements
  }
}
export const initialState = {
  resellerAgreements: [],
  count: 0,
  pageNumber: 0,
  noOfPage: null,
  pageSize: 10,
  currentResaleAuthorization: {},
}
type actionType =
  | {
      type: ResaleAuthorizationsActionTypes.RESALE_SET_RESELLER_DATA
      payload: {
        agreements: ResaleAuthorizationsState
        pageSize: number
        pageNumber: number
      }
    }
  | {
      type: ResaleAuthorizationsActionTypes.RESALE_SET_PAGE_NUMBER
      payload: number
    }
  | {
      type: ResaleAuthorizationsActionTypes.RESALE_SET_LIMIT
      payload: number
    }
  | {
      type: ResaleAuthorizationsActionTypes.UPDATE_AGREEMENT_ID
      payload: {
        agreementId: string
        awsAgreementId: string
      }
    }
  | {
      type: ResaleAuthorizationsActionTypes.UPDATE_AGREEMENT_STATUS
      payload: {
        agreementId: string
        resellerAgreementStatus: ResellerAgreementStatus
      }
    }
  | {
      type: ResaleAuthorizationsActionTypes.RESALE_CLEAR_RESELLER_DATA
    }
  | {
      type: ResaleAuthorizationsActionTypes.SET_CURRENT_RESALE_AUTHORIZATION
      payload: ResaleAuthorizationsResellerAgreements
    }
  | {
      type: ResaleAuthorizationsActionTypes.CLEAR_CURRENT_RESALE_AUTHORIZATION
    }
export const reducer: Reducer<ResaleAuthorizationsState, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case ResaleAuthorizationsActionTypes.RESALE_SET_RESELLER_DATA: {
      const existingIds = new Set(
        state.resellerAgreements.map(agreement => agreement.id)
      )

      const newAgreements = action.payload.agreements.resellerAgreements.filter(
        agreement => !existingIds.has(agreement.id)
      )
      return {
        ...state,
        resellerAgreements: state.resellerAgreements.concat(newAgreements),
        count: action.payload.agreements.count,
        pageSize: action.payload.pageSize,
        pageNumber: action.payload.pageNumber,
        noOfPage: Math.ceil(
          action.payload.agreements.count / action.payload.pageSize
        ),
      }
    }
    case ResaleAuthorizationsActionTypes.UPDATE_AGREEMENT_ID: {
      const { agreementId, awsAgreementId } = action.payload
      const resellerAgreements = [...state.resellerAgreements].map(
        agreement => {
          if (agreement.id === agreementId) {
            return {
              ...agreement,
              awsAgreementId,
            }
          } else {
            return agreement
          }
        }
      )
      return {
        ...state,
        resellerAgreements,
      }
    }
    case ResaleAuthorizationsActionTypes.UPDATE_AGREEMENT_STATUS: {
      const { agreementId, resellerAgreementStatus } = action.payload
      const resellerAgreements = [...state.resellerAgreements].map(
        agreement => {
          if (agreement.id === agreementId) {
            return {
              ...agreement,
              status: resellerAgreementStatus,
            }
          } else {
            return agreement
          }
        }
      )
      return {
        ...state,
        resellerAgreements,
      }
    }
    case ResaleAuthorizationsActionTypes.RESALE_SET_LIMIT: {
      return {
        ...state,
        pageSize: action.payload,
      }
    }
    case ResaleAuthorizationsActionTypes.RESALE_SET_PAGE_NUMBER: {
      return {
        ...state,
        pageNumber: action.payload,
      }
    }
    case ResaleAuthorizationsActionTypes.RESALE_CLEAR_RESELLER_DATA: {
      return {
        ...state,
        resellerAgreements: [],
        noOfPage: null,
        count: 0,
      }
    }
    case ResaleAuthorizationsActionTypes.SET_CURRENT_RESALE_AUTHORIZATION: {
      return {
        ...state,
        currentResaleAuthorization: {
          [action.payload.id]:
            action.payload as ResaleAuthorizationsResellerAgreements,
        },
      }
    }
    case ResaleAuthorizationsActionTypes.CLEAR_CURRENT_RESALE_AUTHORIZATION: {
      return {
        ...state,
        currentResaleAuthorization: {},
      }
    }
    default:
      return state
  }
}
